import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import ScrollToHash from "@/hooks/useScrollHash";
import ScrollToTop from "@/hooks/useScrollTop";
import Layout from "@/layouts/Layout";
import Loading from "@/components/Loading";
import ShopSkeleton from "@/components/skeletons/ShopSkeleton";

const Cart = lazy(() => import("@/pages/Shop/Cart"));
import Home from "@/pages/Home";
const Shop = lazy(() => import("@/pages/Shop/Shop"));
const ProductDetail = lazy(() => import("@/pages/Shop/ProductDetail"));
const Favorites = lazy(() => import("@/pages/Shop/Favorites"));
const Checkout = lazy(() => import("@/pages/Shop/Checkout"));
const CheckoutSuccess = lazy(() => import("@/pages/Shop/CheckoutSuccess"));
const OrderList = lazy(() => import("@/pages/Shop/OrderList"));
const OrderDetail = lazy(() => import("@/pages/Shop/OrderDetail"));
const Profile = lazy(() => import("@/pages/Profile/Profile"));
const EditProfile = lazy(() => import("@/pages/Profile/EditProfile"));
const AddressList = lazy(() => import("@/pages/Profile/AddressList"));
const EditAddress = lazy(() => import("@/pages/Profile/EditAddress"));
const FarmList = lazy(() => import("@/pages/Farms/FarmList"));
const EditFarm = lazy(() => import("@/pages/Farms/EditFarm"));
const Login = lazy(() => import("@/pages/Auth/Login"));
const Register = lazy(() => import("@/pages/Auth/Register"));
const VerifyEmail = lazy(() => import("@/pages/Auth/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/Auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/Auth/ResetPassword"));
const PaymentGuide = lazy(() => import("@/pages/PaymentGuide"));
const Vouchers = lazy(() => import("@/pages/Vouchers"));
const Farms = lazy(() => import("@/pages/Farms/Farms"));
const FarmDetail = lazy(() => import("@/pages/Farms/FarmDetail"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import AppsContext from "@/layouts/AppsContext";
import ShopContextPage from "@/pages/Shop/ShopContextPage";
import { AuthProvider } from "@/layouts/AuthContext";
import AuthGuard from "./AuthGuard";
import GuestGuard from "./GuestGuard";
import FarmContextPage from "@/pages/Farms/FarmContextPage";

export default function RouterList() {
    return (
        <AppsContext>
            <AuthProvider>
                <Layout>
                    <ScrollToTop />
                    <ScrollToHash />
                    <Suspense fallback={<Loading />}>
                        <Routes>
                            {/* halaman depan */}
                            <Route index element={<Home />} />
                            <Route
                                path="payment-guide"
                                element={<PaymentGuide />}
                            />
                            <Route path="vouchers" element={<Vouchers />} />
                            <Route path="farm" element={<Farms />} />
                            <Route path="farm/:slug" element={<FarmDetail />} />
                            <Route path="about" element={<About />} />
                            <Route path="contact" element={<Contact />} />
                            <Route
                                path="terms-and-conditions"
                                element={<TermsAndConditions />}
                            />

                            {/* shop dan turunannya */}
                            <Route
                                path="shop"
                                element={
                                    <ShopContextPage>
                                        <Suspense fallback={<ShopSkeleton />}>
                                            <Shop />
                                        </Suspense>
                                    </ShopContextPage>
                                }
                            />
                            <Route
                                path="shop/:slug"
                                element={
                                    <ShopContextPage>
                                        <Suspense fallback={<ShopSkeleton />}>
                                            <ProductDetail />
                                        </Suspense>
                                    </ShopContextPage>
                                }
                            />

                            {/* auth */}
                            <Route element={<GuestGuard />}>
                                <Route path="login" element={<Login />} />
                                <Route path="register" element={<Register />} />
                                <Route
                                    path="forgot-password"
                                    element={<ForgotPassword />}
                                />
                                <Route
                                    path="reset-password"
                                    element={<ResetPassword />}
                                />
                            </Route>

                            {/* with auth */}
                            <Route element={<AuthGuard />}>
                                <Route
                                    path="verify-email"
                                    element={<VerifyEmail />}
                                />
                                <Route path="cart" element={<Cart />} />
                                <Route
                                    path="favorites"
                                    element={<Favorites />}
                                />
                                <Route path="checkout" element={<Checkout />} />
                                <Route
                                    path="checkout/success/:orderNumber?"
                                    element={<CheckoutSuccess />}
                                />
                                <Route path="orders" element={<OrderList />} />
                                <Route
                                    path="orders/:id"
                                    element={<OrderDetail />}
                                />

                                {/* profile */}
                                <Route path="profile" element={<Profile />} />
                                <Route
                                    path="profile/edit"
                                    element={<EditProfile />}
                                />
                                <Route
                                    path="profile/address"
                                    element={<AddressList />}
                                />
                                <Route
                                    path="profile/address/add"
                                    element={<EditAddress />}
                                />
                                <Route
                                    path="profile/address/edit/:id"
                                    element={<EditAddress />}
                                />
                                <Route
                                    path="profile/farm"
                                    element={
                                        <FarmContextPage>
                                            <FarmList />
                                        </FarmContextPage>
                                    }
                                />
                                <Route
                                    path="profile/farm/new"
                                    element={
                                        <FarmContextPage>
                                            <EditFarm />
                                        </FarmContextPage>
                                    }
                                />
                                <Route
                                    path="profile/farm/edit/:id"
                                    element={
                                        <FarmContextPage>
                                            <EditFarm />
                                        </FarmContextPage>
                                    }
                                />
                            </Route>

                            {/* not found catch-all */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                </Layout>
            </AuthProvider>
        </AppsContext>
    );
}
